/* eslint-disable max-lines */
import { useMemo, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { toast, Typography, Stack } from "@altimateai/lego";
import { getAllQueries, getQueryFilters } from "@/helpers/apis";
import { useAppState } from "@/modules/app/useAppContext";
import { formatNumber } from "@/uiCore";
import { getInstanceName } from "@/helpers/utils";
import useFiltersBySearchParams from "@/helpers/useFiltersBySearchParams";
import DataTable from "@/components/tables/DataTable";
import { useTablePagination } from "@/components/tables/hooks/useTablePagination";
import { Paginate } from "@/components/paginate";
import { FilterType, TableHeaderType, createAPIDropdownFilter } from "@/components/tables/types";
import { FilterApplicableFor } from "@/components/tags/types";
import { ColorInsightTags } from "@/components/tags";
import { GET_QUERY } from "../constants";
import { FilterEntityType, QueryItem } from "../types";
import QueryTextWithHover from "../QueryText";
import QueryHashWithHover from "../QueryHash";
import InsightLabel from "../InsightLabel";
import AllQueriesDownload from "./AllQueriesDownload";
import AllQueriesHeader from "./AllQueriesHeader";
import { useAllQueriesFilters } from "./useAllQueriesFilters";
import { DebouncedInput } from "@altimateai/lego";
import { IconSearch } from "@tabler/icons-react";

interface AllQueriesListWithFilterProps {
  getRowUrl: (item: QueryItem) => string;
  getRowState?: (item: QueryItem) => Record<string, unknown>;
  queryHash?: string;
}

const AllQueriesListWithFilter = ({ getRowUrl, getRowState, queryHash = "" }: AllQueriesListWithFilterProps) => {
  const { currencySymbol, currency, datastores = [] } = useAppState();
  const { updateSearchParamsByKey } = useFiltersBySearchParams();

  // Pagination from context
  const {
    page,
    pageSize,
    setPage,
    setPageSize: updatePageSize,
    resetPage,
    setTotalPages,
    setTotalItems,
    totalItems,
    totalPages,
  } = useTablePagination();

  // Use filters hook (sort state is now in URL params)
  const {
    filterParams,
    isInitialized,
    queryId,
    executionCostFilterMode,
    executionTimeFilterMode,
  } = useAllQueriesFilters({
    queryHash,
  });

  // Fetch queries
  const {
    data: queries = {
      total: 0,
      items: [],
      pages: 1,
    },
    isFetching: isQueriesLoading,
  } = useQuery({
    queryKey: [GET_QUERY, filterParams],
    queryFn: () => getAllQueries(filterParams),
    enabled: isInitialized,
    meta: {
      onError: (err: Error) => {
        toast({
          title: "Failed to fetch queries",
          description: `${err?.message || err} Please try again.`,
          variant: "destructive",
        });
      },
    },
  });

  // Update pagination totals when query data changes
  useEffect(() => {
    if (queries) {
      setTotalItems(queries.total);
      setTotalPages(queries.pages);
    }
  }, [queries, setTotalItems, setTotalPages]);

  const getInstanceNameFromId = (instanceId: number) => {
    return getInstanceName(instanceId, datastores);
  };

  // Table headers with filters
  const headers: TableHeaderType<QueryItem>[] = useMemo(
    () => [
      {
        id: 1,
        label: "Query Text",
        uid: "query_text",
        width: "250px",
        render: item => (
          <div className="al-break-words al-text-brand al-cursor-pointer">
            <QueryTextWithHover queryText={item.query_trimmed} queryHash={item.query_hash} />
          </div>
        ),
      },
      {
        id: 2,
        label: "Query Hash",
        uid: "query_hash",
        width: "180px",
        render: item => (
          <div className="al-break-words al-cursor-pointer">
            <QueryHashWithHover queryHash={item.query_hash} />
          </div>
        ),
      },
      {
        id: 3,
        label: "Est. Cost",
        uid: "est_cost",
        width: "120px",
        filter: {
          filterType: FilterType.TEXT,
          placeHolder: `Specify cost in ${currencySymbol}`,
          searchKey: "queryCost",
          label: "Estimate Cost",
          filterMode: executionCostFilterMode,
          filterModeSearchKey: "executionCostFilterMode",
        },
        sort: {
          sortSearchKey: "sortAttribute",

          sortParamValue: "query_cost",
        },
        render: item => (
          <div className="al-break-all">{formatNumber(item.query_cost, { currency })}</div>
        ),
      },
      {
        id: 4,
        label: "Exec. Time",
        uid: "exec_time",
        width: "120px",
        filter: {
          filterType: FilterType.TEXT,
          placeHolder: "Specify time in mins",
          searchKey: "executionTime",
          label: "Execution Time",
          filterMode: executionTimeFilterMode,
          filterModeSearchKey: "executionTimeFilterMode",
        },
        sort: {
          sortSearchKey: "sortAttribute",

          sortParamValue: "execution_time",
        },
        render: item => <div className="al-break-all">{item.execution_time}</div>,
      },
      {
        id: 5,
        label: "Insights",
        uid: "insights",
        width: "200px",
        filter: createAPIDropdownFilter({
          filterType: FilterType.API_DROPDOWN,
          searchKey: "tags",
          apiFunction: ({ prefix, page }) =>
            getQueryFilters({ entity_type: FilterEntityType.TAGS, prefix, page }),
          seeMore: true,
          pageSize: 50,
          extractorFunction: data => {
            return (
              data?.tags?.map(t => ({
                label: <InsightLabel tag={t} />,
                value: t.name,
                showTooltip: false,
              })) || []
            );
          },
        }),
        render: item => <ColorInsightTags tags={item?.insights || []} showMore />,
      },
      {
        id: 6,
        label: "Timestamp",
        uid: "timestamp",
        width: "180px",
        sort: {
          sortSearchKey: "sortAttribute",

          sortParamValue: "end_time",
        },
        render: item => <div>{item.end_time}</div>,
      },
      {
        id: 7,
        label: "Query Type",
        uid: "query_type",
        width: "120px",
        filter: createAPIDropdownFilter({
          filterType: FilterType.API_DROPDOWN,
          searchKey: "queryType",
          apiFunction: ({ prefix, page }) =>
            getQueryFilters({ entity_type: FilterEntityType.QUERY_TYPES, prefix, page }),
          pageSize: 50,
          seeMore: true,
          extractorFunction: data => {
            return (
              data?.query_types?.map(qt => ({
                label: qt,
                value: qt,
              })) || []
            );
          },
        }),
        render: item => <div className="al-break-all">{item.query_type}</div>,
      },
      {
        id: 8,
        label: "Warehouse",
        uid: "warehouse",
        width: "150px",
        filter: createAPIDropdownFilter({
          filterType: FilterType.API_DROPDOWN,
          searchKey: "warehouses",
          apiFunction: ({ prefix, page }) =>
            getQueryFilters({ entity_type: FilterEntityType.WAREHOUSES, prefix, page }),
          pageSize: 50,
          seeMore: true,
          extractorFunction: data => {
            return (
              data?.warehouses?.map(w => ({
                label: w,
                value: w,
              })) || []
            );
          },
        }),
        render: item => (
          <Link
            to={"/warehouse?warehouse=" + encodeURIComponent(item.warehouse_name)}
            className="al-break-all al-text-primary"
            onClick={e => e.stopPropagation()}
          >
            {item.warehouse_name}
          </Link>
        ),
      },
      {
        id: 9,
        label: "User",
        uid: "user",
        width: "150px",
        filter: createAPIDropdownFilter({
          filterType: FilterType.API_DROPDOWN,
          searchKey: "users",
          apiFunction: ({ prefix, page }) =>
            getQueryFilters({ entity_type: FilterEntityType.USERS, prefix, page }),
          pageSize: 50,
          seeMore: true,
          extractorFunction: data => {
            return (
              data?.users?.map(u => ({
                label: u,
                value: u,
              })) || []
            );
          },
        }),
        render: item => <div className="al-break-all">{item.user_name}</div>,
      },
      {
        id: 10,
        label: "Instance",
        uid: "instance_id",
        width: "120px",
        filter: {
          filterType: FilterType.DROPDOWN,
          searchKey: "instance",
          options: datastores
            .filter(instance => instance.instance_id != null)
            .map(instance => ({
              label: instance.name,
              value: String(instance.instance_id),
            })),
        },
        render: item => (
          <div className="al-break-all">{getInstanceNameFromId(item.instance_id)}</div>
        ),
      },
    ],
    [
      currencySymbol,
      currency,
      datastores,
      executionCostFilterMode,
      executionTimeFilterMode,
    ]
  );

  const isLoading = isQueriesLoading;

  return (
    <Stack direction="column" className="al-bg-white al-rounded-md">
      {queryHash && (
        <Typography variant="h4" className="al-p-3">
          Query Executions
        </Typography>
      )}
      <AllQueriesHeader />
      <Stack direction="column" gap="md" className="al-p-3">
        <Stack direction="row" justify="between" align="center" gap="md">
          <Stack direction="row" align="center" gap="md" className={"al-w-full"}>
            <DebouncedInput
              icon={IconSearch}
              debounceMs={1000}
              defaultValue={queryId}
              onChange={queryId => {
                updateSearchParamsByKey({ queryId });
              }}
              className={"!al-w-1/3"}
              placeholder="Enter query id"
            />
            {queryId && queries.total === 0 && (
              <Typography variant="body" size="xs" className="al-text-gray-500">
                The query id may be outdated, or it will be retrieved within the next day
              </Typography>
            )}
          </Stack>
          <Stack align="center" gap="md">
            <AllQueriesDownload queryCount={totalItems} />
            <div id="columnpicker-container" />
          </Stack>
        </Stack>
        <DataTable<QueryItem>
          header={headers}
          getRowUrl={getRowUrl}
          getRowState={getRowState}
          data={queries}
          resetPage={resetPage}
          applicableFor={FilterApplicableFor.QUERY}
          columnPickerPortal="columnpicker-container"
          columnPickerProps={{ buttonProps: { variant: "outline" } }}
          minWidth={1600}
          isLoading={isLoading}
          portals={{
            filter: "filter-container",
            savedFilters: "saved-filters-container",
          }}
          defaultSort={{ sortParamValue: "query_cost", order: "desc" }}
        />
        <Paginate
          itemCount={totalItems}
          page={page}
          pageSize={pageSize}
          numPages={totalPages}
          onPageClick={setPage}
          onPageSizeChange={updatePageSize}
          showPageSize
          isLoading={isLoading}
        />
      </Stack>
    </Stack>
  );
};

export default AllQueriesListWithFilter;
